import type { App, AppConfig } from 'vue';

import type { ToastPresetType } from '@modules/toast/types';
import {
  AccessDeniedError,
  BaseAppError,
  ServerBaseError,
  ServerUnauthorizedError,
} from '@modules/core/errors';
import { isInErrorExclusionList, SENTRY_IGNORED_ERROR_NAMES_LIST } from '@modules/core/errors/errorExclusions';
import createSimpleErrorApp from '@modules/error-pages/utils/createSimpleErrorApp';
import { useToastStore } from '@modules/toast/store';
import { useUserStore } from '@modules/user/store';
import * as Sentry from '@sentry/vue';

export function captureSentryException(error: unknown): void {
  if (isInErrorExclusionList(error, SENTRY_IGNORED_ERROR_NAMES_LIST)) {
    return;
  }
  Sentry.captureException(error);
}

function handleAppError(error: unknown): boolean {
  if (error instanceof ServerUnauthorizedError) {
    const userStore = useUserStore();
    const { signOut, goToSupervision } = userStore;
    const { displayPresetToast } = useToastStore();
    displayPresetToast('error', error.message);

    if (error.redirect_to_supervision && error?.currentUser?.id) {
      void goToSupervision(error.currentUser);
    } else if (!userStore.isSignOutLoading) {
      void signOut();
    }

    return true;
  }

  if (error instanceof ServerBaseError && error.showErrorPage) {
    createSimpleErrorApp();
    return true;
  }

  if (error instanceof BaseAppError && error.showToast) {
    const { displayPresetToast } = useToastStore();
    const message = error.showMessageInToast ? error.message : '';
    const presetType: ToastPresetType = error instanceof AccessDeniedError ? 'access_error' : 'error';

    displayPresetToast(presetType, message);

    return true;
  }

  return false;
}

const vueErrorHandler: AppConfig['errorHandler'] = (error: unknown, vm, info) => {
  if (handleAppError(error)) {
    return;
  }

  captureSentryException(error);

  const errorMsg = error instanceof Error ? error.message : JSON.stringify(error);

  let errorMessage = `[Vue ERROR]: ${errorMsg}`;

  // if the error is empty determine component by props
  if (!errorMessage && vm && vm.$props) {
    errorMessage = `[ComponentProps]:${JSON.stringify(vm.$props)}`;
  }

  let message = `[Global Error Handler]: Error in ${info}:${errorMessage}`;

  if (vm && vm.$options && vm.$options.name) {
    message += `[Component]: ${vm.$options.name}`;
  }

  console.error(message, error instanceof Error ? error.stack ?? '' : '');
};

const vueWarnHandler: AppConfig['warnHandler'] = (error, vm, trace) => {
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    Sentry.captureException(error);
  });

  let message = '[Vue warning]: ';

  if (error) {
    message += error;
  }

  if (vm?.$options?.name) {
    message += `[Component]: ${vm.$options.name}`;
  }

  console.warn({
    message,
    stacktrace: trace || '',
  });
};

function unhandlerPromiseRejectionError(event: PromiseRejectionEvent): void {
  try {
    const { reason } = event;

    if (handleAppError(reason)) {
      return;
    }

    const details: string[] = [];
    details.push(`event: ${JSON.stringify({
      timeStamp: event.timeStamp,
      isTrusted: event.isTrusted,
      isRootTarget: event.target === window.event?.target,
    })}`);

    if (reason instanceof Error) {
      details.push(`reason E: ${JSON.stringify(reason, ['message', 'arguments', 'type', 'name'])}`);
    } else {
      details.push(`reason: ${JSON.stringify(reason)}`);
    }

    console.error(
      `Unhandled Promise Rejection: ${details.join(';')}`,
      reason?.stack || new Error('error').stack || 'Unknown stack',
    );
  } catch (error) {
    console.error('Unhandled Promise Rejection processing error', error);
  }
}

export default function createErrorHandler(app: App): void {
  app.config.errorHandler = vueErrorHandler;

  app.config.warnHandler = vueWarnHandler;

  window.addEventListener('unhandledrejection', unhandlerPromiseRejectionError);
}
